import {
  Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState,
  type CSSProperties, type HTMLAttributes, type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { BLANK, GRID_WORDING, facetCounts, multiSort, slicerCount, sliceRows, type Facet } from '@lib/grid';
import type { GridColumn, GridView } from './useGridView';

/**
 * Row 356 (approval 169): the shared table. Every Care table renders through these parts, so the
 * "Columns" tick list, the drag grips, click and Shift-click sorting, the slicers and Expand behave
 * the same on every screen, and the same as the ClariConnect console's grid.
 *
 * Two ways to use it:
 *   - <CareGrid> for a table whose rows all arrive at once: it sorts and slices in the browser over
 *     the WHOLE list and says how many rows the slicers hide.
 *   - The parts (GridToolbar, GridSlicerBar, GridHiddenLine, GridHeadRow, gridCell) for a table that
 *     holds one page or virtualizes its body: the page sends view.sort and view.slicers to its
 *     server and passes the server's facet counts and hidden count back in.
 */

// ---------------------------------------------------------------------------------------------
// Cells

function widthStyle(w: number | undefined): CSSProperties | undefined {
  return w ? { width: w, maxWidth: w, minWidth: w } : undefined;
}

/** The props of one body cell: the column's own style plus the operator's width. */
export function gridCell<T>(view: GridView<T>, col: GridColumn<T>, row: T): HTMLAttributes<HTMLTableCellElement> {
  const w = view.widths[col.key];
  const own = typeof col.tdStyle === 'function' ? col.tdStyle(row) : col.tdStyle;
  const cls = typeof col.tdClassName === 'function' ? col.tdClassName(row) : col.tdClassName;
  const style: CSSProperties = { ...(col.align ? { textAlign: col.align } : null), ...own, ...widthStyle(w) };
  return {
    style: Object.keys(style).length ? style : undefined,
    className: [cls, w ? 'grid-cell-fixed' : ''].filter(Boolean).join(' ') || undefined,
    ...(col.stopRowClick ? { onClick: (e: React.MouseEvent) => e.stopPropagation() } : null),
  };
}

// ---------------------------------------------------------------------------------------------
// Header

function HeadCell<T>({ view, col }: { view: GridView<T>; col: GridColumn<T> }): JSX.Element {
  const ref = useRef<HTMLTableCellElement>(null);
  const [dragging, setDragging] = useState(false);
  const w = view.widths[col.key];
  const i = view.sort.findIndex((s) => s.k === col.key);
  const sortable = view.isSortable(col.key);
  const label = view.columnLabel(col);

  const onGrip = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    const x0 = ev.clientX;
    const w0 = ref.current?.getBoundingClientRect().width ?? 120;
    let last = w0;
    setDragging(true);
    const mv = (e: MouseEvent) => {
      last = w0 + e.clientX - x0;
      view.setWidth(col.key, last, false);
    };
    const up = () => {
      document.removeEventListener('mousemove', mv);
      document.removeEventListener('mouseup', up);
      setDragging(false);
      view.setWidth(col.key, last, true);
    };
    document.addEventListener('mousemove', mv);
    document.addEventListener('mouseup', up);
  };

  const arrow = i >= 0 ? (view.sort[i]!.asc ? '▴' : '▾') : '⇅';
  return (
    <th
      ref={ref}
      className={['grid-th', col.thClassName].filter(Boolean).join(' ')}
      style={{ ...(col.align ? { textAlign: col.align } : null), ...col.thStyle, ...widthStyle(w) }}
      aria-sort={i === 0 ? (view.sort[0]!.asc ? 'ascending' : 'descending') : sortable ? 'none' : undefined}
      title={col.title}
      data-col={col.key}
    >
      {sortable ? (
        <button
          type="button"
          className="grid-sort-btn"
          title={col.title ? `${col.title}. ${GRID_WORDING.sortTip}` : GRID_WORDING.sortTip}
          // His word, 16 Oct 2026: "We don't then need the shift and click." A heading click sorts by
          // that one column (again flips it); Shift makes no difference.
          onClick={() => view.clickHeader(col.key, false)}
        >
          {col.label}
          <span aria-hidden="true" className={i >= 0 ? 'grid-arrow on' : 'grid-arrow'}>{arrow}</span>
          {i >= 0 && view.sort.length > 1 && <span className="grid-sortn" aria-label={`sort key ${i + 1}`}>{i + 1}</span>}
        </button>
      ) : (
        col.label
      )}
      <span
        className={dragging ? 'grid-grip dragging' : 'grid-grip'}
        onMouseDown={onGrip}
        onClick={(e) => e.stopPropagation()}
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${label}`}
        title={GRID_WORDING.columnsTip}
      />
    </th>
  );
}

/** The heading row for the visible columns. */
export function GridHeadRow<T>({ view }: { view: GridView<T> }): JSX.Element {
  return (
    <tr>
      {view.visible.map((c) => (
        <HeadCell key={c.key} view={view} col={c} />
      ))}
    </tr>
  );
}

// ---------------------------------------------------------------------------------------------
// Toolbar: Columns, Expand, Reset, the sort hint and the refusal notice

export function GridColumnsButton<T>({ view }: { view: GridView<T> }): JSX.Element {
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [open]);
  const shown = view.columns.length - view.hidden.size;
  return (
    <span className="grid-cols-wrap" ref={wrap}>
      <button
        type="button"
        className="btn btn-sm btn-ghost"
        aria-expanded={open}
        title={GRID_WORDING.columnsTip}
        onClick={() => setOpen((o) => !o)}
      >
        {GRID_WORDING.columns} ▾{view.hidden.size > 0 ? ` (${shown} of ${view.columns.length})` : ''}
      </button>
      {open && (
        <div className="grid-cols-menu" role="group" aria-label={GRID_WORDING.columns}>
          {view.columns.map((c) => (
            <label key={c.key} className={c.locked ? 'locked' : undefined} title={c.locked ? GRID_WORDING.alwaysShown : undefined}>
              <input type="checkbox" checked={!view.hidden.has(c.key)} disabled={c.locked} onChange={() => view.toggleColumn(c.key)} />
              {view.columnLabel(c)}
              {c.locked && <span className="muted small">({GRID_WORDING.alwaysShown})</span>}
            </label>
          ))}
          <div className="grid-cols-all">
            <button type="button" onClick={() => view.resetColumns()}>
              {view.columns.some((c) => c.offByDefault) ? GRID_WORDING.resetWithExtras : GRID_WORDING.reset}
            </button>
          </div>
        </div>
      )}
    </span>
  );
}

export function GridToolbar<T>({ view, left }: { view: GridView<T>; left?: ReactNode }): JSX.Element {
  // His word, 16 Oct 2026: the sort hint line is removed completely.
  return (
    <div className="grid-toolbar">
      {left}
      {view.notice && (
        <span className="pill pill-warning" role="status">
          {view.notice}
        </span>
      )}
      <span className="grid-right">
        {view.customized && (
          <button type="button" className="btn btn-sm btn-ghost" title={GRID_WORDING.resetTip} onClick={() => view.resetAll()}>
            {GRID_WORDING.resetAll}
          </button>
        )}
        <GridColumnsButton view={view} />
        <button
          type="button"
          className="btn btn-sm btn-ghost"
          aria-pressed={view.expanded}
          title={view.expanded ? GRID_WORDING.collapseTip : GRID_WORDING.expandTip}
          onClick={() => view.setExpanded(!view.expanded)}
        >
          {view.expanded ? GRID_WORDING.collapse : GRID_WORDING.expand}
        </button>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Slicers

function SlicerGroup<T>({ view, defKey, facets }: { view: GridView<T>; defKey: string; facets: Facet[] }): JSX.Element | null {
  const def = view.slicerDefs.find((d) => d.key === defKey)!;
  const [q, setQ] = useState('');
  const chosen = view.slicers[defKey] ?? [];
  const labelOf = (v: string) => (v === BLANK ? def.blankLabel ?? v : def.labelFor ? def.labelFor(v) : v);
  // A slicer with one value and nothing chosen narrows nothing: not offered.
  if (facets.length <= 1 && chosen.length === 0) return null;
  const past = def.searchPast ?? 12;
  const many = facets.length > past;
  const needle = q.trim().toLowerCase();
  const pool = many
    ? facets.filter((f) => chosen.includes(f.v) || (needle && labelOf(f.v).toLowerCase().includes(needle))).slice(0, past + chosen.length)
    : facets;
  return (
    <div className="grid-slicer" role="group" aria-label={def.label}>
      <span className="grid-slicer-label">{def.label}</span>
      <button type="button" className={chosen.length ? 'grid-chip' : 'grid-chip on'} onClick={() => view.setSlicerValues(defKey, [])} aria-pressed={!chosen.length}>
        {GRID_WORDING.slicersAll}
      </button>
      {many && (
        <input
          type="search"
          className="grid-slicer-search"
          aria-label={GRID_WORDING.slicerSearch(def.label)}
          placeholder={GRID_WORDING.slicerSearch(def.label)}
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      )}
      {pool.map((f) => {
        const on = chosen.includes(f.v);
        return (
          <button key={f.v} type="button" className={on ? 'grid-chip on' : 'grid-chip'} aria-pressed={on} onClick={() => view.toggleSlicerValue(defKey, f.v)}>
            {labelOf(f.v)} <span className="n">{f.n.toLocaleString()}</span>
          </button>
        );
      })}
    </div>
  );
}

/** One group of buttons per slicer, each value with how many rows it would show. */
export function GridSlicerBar<T>({ view, facets }: { view: GridView<T>; facets: Record<string, Facet[]> }): JSX.Element | null {
  if (view.slicerDefs.length === 0) return null;
  const groups = view.slicerDefs.map((d) => <SlicerGroup key={d.key} view={view} defKey={d.key} facets={facets[d.key] ?? []} />);
  return <div className="grid-slicers">{groups}</div>;
}

/**
 * THE ROW 354 LINE: what the slicers are hiding, SAID, with one click back to everything. Shown
 * whenever a slicer is set, even at 0 hidden, so a narrowed view never looks like the whole list.
 */
export function GridHiddenLine<T>({
  view, hidden, keptByRule, noun, protectWhat,
}: {
  view: GridView<T>;
  hidden: number;
  keptByRule?: number | undefined;
  noun: string;
  protectWhat?: string | undefined;
}): JSX.Element | null {
  if (slicerCount(view.slicers) === 0) return null;
  return (
    <div className={hidden > 0 ? 'note warn grid-hidden-line' : 'note grid-hidden-line'} role="status" data-testid="grid-hidden-line">
      <span>{GRID_WORDING.hidden(hidden, noun)}</span>
      {protectWhat && (keptByRule ?? 0) > 0 && <span>{GRID_WORDING.protectedNote(protectWhat)}</span>}
      <button type="button" className="btn btn-sm btn-ghost" onClick={() => view.clearSlicers()}>
        {GRID_WORDING.clear}
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Expand

/**
 * Expand lays the table over the whole screen (the page's content is capped in width). The place it
 * left keeps its height so the page under it does not move; Esc or the button brings it back. It
 * sits under the drawer, so opening a row still works while expanded.
 */
export function GridFrame<T>({ view, children, testId }: { view: GridView<T>; children: ReactNode; testId?: string | undefined }): JSX.Element {
  const box = useRef<HTMLDivElement>(null);
  const [held, setHeld] = useState(0);
  useLayoutEffect(() => {
    if (!view.expanded && box.current) setHeld(box.current.offsetHeight);
  }, [view.expanded]);
  useEffect(() => {
    if (!view.expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.drawer')) return; // the drawer's own Esc goes first
      view.setExpanded(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [view.expanded, view]);
  return (
    <div style={view.expanded && held ? { minHeight: held } : undefined} data-testid={testId}>
      <div
        ref={box}
        className={view.expanded ? 'grid-frame grid-expanded' : 'grid-frame'}
        role={view.expanded ? 'dialog' : undefined}
        aria-modal={view.expanded ? false : undefined}
        aria-label={view.expanded ? view.table : undefined}
      >
        {children}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------
// Browser-side rows

/** Sort and slice over the WHOLE list, and count what the slicers hide. */
export function useGridRows<T>(view: GridView<T>, rows: readonly T[], protect?: (r: T) => boolean) {
  const cols = view.columns;
  const sliced = useMemo(() => sliceRows(rows, view.slicerDefs, view.slicers, protect), [rows, view.slicerDefs, view.slicers, protect]);
  const facets = useMemo(() => facetCounts(rows, view.slicerDefs, view.slicers), [rows, view.slicerDefs, view.slicers]);
  const sorted = useMemo(() => {
    if (view.sort.length === 0) return sliced.rows;
    const byKey = new Map(cols.map((c) => [c.key, c]));
    return multiSort(sliced.rows, view.sort, (r, k) => byKey.get(k)?.sortValue?.(r));
  }, [sliced.rows, view.sort, cols]);
  return { rows: sorted, hidden: sliced.hidden, keptByRule: sliced.keptByRule, facets };
}

// ---------------------------------------------------------------------------------------------
// The whole table

export interface CareGridProps<T> {
  view: GridView<T>;
  /** Every row (browser mode), or the page the server already sorted and sliced (server mode). */
  rows: readonly T[];
  rowKey: (r: T) => string;
  /** What a row is called in the hidden line ("events", "rows"). */
  noun: string;
  className?: string;
  tableStyle?: CSSProperties;
  /** Rows no slicer may hide (row 354: what a member started with no campaign). */
  protect?: (r: T) => boolean;
  protectWhat?: string;
  /** Server mode: the server sorted and sliced; these are its counts. */
  server?: { facets: Record<string, Facet[]>; hidden: number; keptByRule?: number };
  rowProps?: (r: T, i: number) => HTMLAttributes<HTMLTableRowElement> & { 'data-testid'?: string };
  /** A full-width row under a row (a detail or an expansion). */
  after?: (r: T, i: number) => ReactNode;
  /**
   * A second line of the SAME row (William, 16 Oct 2026 8:30 PM: the message text under each queue row):
   * it starts under the first column whose key is not in `lead` (the tick box and the row number stay
   * alone on their line) and runs to the end. It carries the row's own style and click, so the band
   * covers both lines of one item. Nothing returned = no second line.
   */
  line2?: { lead: readonly string[]; render: (r: T, i: number) => ReactNode } | undefined;
  /** What the body shows when there are no rows at all (Loading, the empty sentence). */
  empty?: ReactNode;
  toolbarLeft?: ReactNode;
  /** Between the toolbar and the table (a stale note, a scope note). */
  above?: ReactNode;
  /** A box around the table alone (the queues sit it in a flush card). */
  tableWrapClassName?: string;
  tableWrapStyle?: CSSProperties;
  /**
   * A fixed-layout table (the Exceptions list): each visible column gets its planned width in a
   * <colgroup>, or the width the operator dragged it to, and the one with no plan takes the rest.
   */
  plannedWidths?: boolean;
  testId?: string;
  /**
   * His Care console page, img6 (24 Sep 2026): the slicers drawn in the page's right-side rail
   * instead of over the table. Absent = over the table, as on every other screen. null = the rail is
   * closed (or not mounted yet), so none are drawn; the hidden-rows line stays over the table either
   * way. Expanded, they come back over the table, since the full-screen table covers the rail.
   */
  slicerHost?: HTMLElement | null | undefined;
}

function TableWrap({ className, style, children }: { className?: string | undefined; style?: CSSProperties | undefined; children: ReactNode }): JSX.Element {
  return className || style ? <div className={className} style={style}>{children}</div> : <>{children}</>;
}

export function CareGrid<T>(p: CareGridProps<T>): JSX.Element {
  const { view } = p;
  const local = useGridRows(view, p.server ? [] : p.rows, p.protect);
  const rows = p.server ? p.rows : local.rows;
  const hidden = p.server ? p.server.hidden : local.hidden;
  const kept = p.server ? p.server.keptByRule ?? 0 : local.keptByRule;
  const facets = p.server ? p.server.facets : local.facets;
  const span = Math.max(1, view.visible.length);
  // How many columns at the start the second line leaves alone (its own blank cells keep them aligned).
  let lead = 0;
  while (p.line2 && lead < view.visible.length && p.line2.lead.includes(view.visible[lead]!.key)) lead += 1;
  if (lead >= span) lead = 0;
  return (
    <GridFrame view={view} testId={p.testId}>
      <GridToolbar view={view} left={p.toolbarLeft} />
      {p.slicerHost === undefined || view.expanded
        ? <GridSlicerBar view={view} facets={facets} />
        : p.slicerHost
          ? createPortal(<GridSlicerBar view={view} facets={facets} />, p.slicerHost)
          : null}
      <GridHiddenLine view={view} hidden={hidden} keptByRule={kept} noun={p.noun} protectWhat={p.protectWhat} />
      {p.above}
      <TableWrap className={p.tableWrapClassName} style={p.tableWrapStyle}>
        <table className={p.className ?? 'dt'} style={p.tableStyle}>
          {p.plannedWidths && (
            <colgroup>
              {view.visible.map((c) => {
                const w = view.widths[c.key] ?? c.defaultWidth;
                return <col key={c.key} style={w !== undefined ? { width: w } : undefined} />;
              })}
            </colgroup>
          )}
          <thead>
            <GridHeadRow view={view} />
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={span} className="muted" style={{ textAlign: 'center', padding: '20px 0' }}>
                  {/* A list the slicers emptied says so, never "nothing here". */}
                  {hidden > 0 ? GRID_WORDING.noneLeft : p.empty}
                </td>
              </tr>
            ) : (
              rows.map((r, i) => {
                const extra = p.after?.(r, i);
                const second = p.line2?.render(r, i);
                const rp = p.rowProps?.(r, i);
                return (
                  <Fragment key={p.rowKey(r)}>
                    <tr {...rp} className={second ? [rp?.className, 'has-line2'].filter(Boolean).join(' ') : rp?.className}>
                      {view.visible.map((c) => (
                        <td key={c.key} {...gridCell(view, c, r)}>
                          {c.render(r, i)}
                        </td>
                      ))}
                    </tr>
                    {second ? (
                      <tr className="row-line2" style={rp?.style} onClick={rp?.onClick} data-testid={`line2-${p.rowKey(r)}`}>
                        {Array.from({ length: lead }, (_x, k) => <td key={`lead-${k}`} />)}
                        <td colSpan={span - lead}>{second}</td>
                      </tr>
                    ) : null}
                    {extra ? (
                      <tr>
                        <td colSpan={span} style={{ padding: 0, height: 'auto' }}>{extra}</td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </TableWrap>
    </GridFrame>
  );
}
